import { PLAN_STATUS } from "@/data/plans";

interface DateInfo {
  label: string;
  value: string;
}

interface GetSubscriptionDateInfoParams {
  planStatus?: string;
  currentPeriodEnd?: number;
  cancelAt?: number | null;
  resumeAt?: number;
  todayTimestamp: number;
}

/**
 * Determines the appropriate date label and formatted value based on subscription status
 */
export function getSubscriptionDateInfo({
  planStatus,
  currentPeriodEnd,
  cancelAt,
  resumeAt,
  todayTimestamp,
}: GetSubscriptionDateInfoParams): DateInfo {
  const isPlanActive = planStatus === PLAN_STATUS.ACTIVE;
  const isPlanPaused = planStatus === PLAN_STATUS.PAUSED;
  const isPlanCancelled = planStatus === PLAN_STATUS.CANCELED;
  const isPlanExpired = planStatus === PLAN_STATUS.EXPIRED;

  // Check if plan will cancel in the future
  const willCancelInFuture = cancelAt
    ? todayTimestamp < cancelAt * 1000
    : false;

  // Format date helper
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Active plan logic
  if (isPlanActive && currentPeriodEnd) {
    if (willCancelInFuture && cancelAt) {
      return {
        label: "EXPIRES ON",
        value: formatDate(cancelAt),
      };
    }
    return {
      label: "RENEWS",
      value: formatDate(currentPeriodEnd),
    };
  }

  // Paused plan logic
  if (isPlanPaused && resumeAt) {
    return {
      label: "RESUME ON",
      value: formatDate(resumeAt),
    };
  }

  // Cancelled plan logic
  if (isPlanCancelled) {
    const expiryDate = cancelAt || currentPeriodEnd;
    if (expiryDate) {
      return {
        label: willCancelInFuture ? "EXPIRES ON" : "EXPIRED ON",
        value: formatDate(expiryDate),
      };
    }
  }

  // Expired plan logic
  if (isPlanExpired && currentPeriodEnd) {
    return {
      label: "EXPIRED ON",
      value: formatDate(currentPeriodEnd),
    };
  }

  // Default fallback
  return {
    label: "RENEWS",
    value: "--",
  };
}
